import React, { useMemo, useCallback, Fragment } from 'react';
import { Switch, Route, useHistory, useRouteMatch } from 'react-router-dom';

import ErrorChiclet from '<src>/components/ErrorChiclet';
import SidebarNavMenu from '<components>/NumbrzMenus/SidebarNavMenu';

import {
  SidebarWrapper,
  SidebarHeader,
  SidebarContent,
  Separator,
} from '<src>/components/NumbrzVerticalEditor';
import { NumbrzTooltip } from '<src>/components/NumbrzButtons';
import { Icon } from 'semantic-ui-react';
import Button from '<src>/components/Button';
import FlowItemList from '../FlowItemListV5';
import { TablesetCounter } from '<src>/sections/jobs/styles';
import SidebarFooter from './Footer';

function renderStage(item, idx) {
  return (
    <>
      <TablesetCounter fontSize="14px">{idx + 1}</TablesetCounter>
      <h4>{item.name}</h4>
    </>
  );
}

export default function FlowSidebar({
  flow,
  api,
  flowSettings = {},
  setFlowSetting,
}) {
  const {
    onAddRunSequenceItem,
    onRemoveRunSequenceItem,
    onReorderRunSequenceItem,
  } = api;

  const match = useRouteMatch();
  const history = useHistory();
  const leftSidebar =
    flowSettings && typeof flowSettings.leftSidebar !== 'undefined'
      ? flowSettings.leftSidebar
      : true;

  const stageHasErrors = useMemo(() => {
    let hasErrors = false;
    flow.stagesV2.forEach((s) => {
      if (!s.runnable) hasErrors = true;
    });
    return hasErrors;
  }, [flow.stagesV2]);
  const testHasErrors = useMemo(() => {
    let hasErrors = false;
    flow.tests.forEach((t) => {
      if (t.warnings.length > 0) hasErrors = true;
    });
    return hasErrors;
  }, [flow.tests]);

  const [jtHaveErrors, wtHaveErrors] = useMemo(() => {
    let jtErrors = false;
    let wtErrors = false;
    flow.data
      .filter((t) => t.prefSource === 'WorkingTable')
      .forEach((t) => {
        if (t.issues.length > 0) wtErrors = true;
      });
    flow.data
      .filter((t) => t.prefSource !== 'WorkingTable')
      .forEach((t) => {
        if (t.issues.length > 0) jtErrors = true;
      });
    return [jtErrors, wtErrors];
  }, [flow.data]);

  const flowNavOpts = [
    {
      text: `Stages`,
      key: 'stages',
      to: `${match.url}/stages`,
      additionalContent: stageHasErrors && (
        <NumbrzTooltip
          position="top"
          content="Stages contain issues"
          trigger={
            <span className="text">
              <ErrorChiclet className="exclamation" sidebar>
                <Icon name="exclamation" />
              </ErrorChiclet>
            </span>
          }
        />
      ),
    },

    {
      text: `Job tables`,
      key: 'jobTables',
      to: `${match.url}/job-tables`,
      additionalContent: jtHaveErrors && (
        <NumbrzTooltip
          position="top"
          content="Job tables contain issues"
          trigger={
            <span className="text">
              <ErrorChiclet className="exclamation" sidebar>
                <Icon name="exclamation" />
              </ErrorChiclet>
            </span>
          }
        />
      ),
    },

    {
      text: `Job variables`,
      key: 'variables',
      to: `${match.url}/variables`,
    },

    {
      text: `Working tables`,
      key: 'workingTables',
      to: `${match.url}/working-tables`,
      additionalContent: wtHaveErrors && (
        <NumbrzTooltip
          position="top"
          content="Working tables contain issues"
          trigger={
            <span className="text">
              <ErrorChiclet className="exclamation" sidebar>
                <Icon name="exclamation" />
              </ErrorChiclet>
            </span>
          }
        />
      ),
    },

    {
      text: `Tests`,
      key: 'flowTesting',
      to: `${match.url}/tests`,
      additionalContent: testHasErrors && (
        <NumbrzTooltip
          position="top"
          content="Tests contain issues"
          trigger={
            <span className="text">
              <ErrorChiclet className="exclamation" sidebar>
                <Icon name="exclamation" />
              </ErrorChiclet>
            </span>
          }
        />
      ),
    },

    {
      text: 'Documentation',
      key: 'documentation',
      to: `${match.url}/documentation`,
    },
  ];

  const runSequence = useMemo(
    () =>
      flow.runSequence.map((rItem) => {
        const item = rItem.stage ? rItem.stage : rItem.flow;
        let stageRef;

        if (rItem.stage) {
          // when stage is updated, the runSequence item needs a ref to pick up those changes
          stageRef = flow.stagesV2.find((st) => st.ID === item.stageID);
        }

        return {
          ...item,
          key: rItem.key,
          valid: stageRef ? stageRef.runnable : item.runnable,
          name: stageRef ? stageRef.name : item.name,
        };
      }),
    [flow.runSequence, flow.stagesV2]
  );

  const onAddStage = useCallback(
    async ({ flowID, stage, flow }) => {
      if (!onAddRunSequenceItem) {
        return null;
      }

      const res = await onAddRunSequenceItem(null, {
        flowID,
        stage,
        flow,
      });
      const newRunSequence =
        res?.data?.addRunSequenceItem?.flow?.runSequence || [];
      const newRunItem = newRunSequence[newRunSequence.length - 1];

      if (newRunItem?.stage?.stageID) {
        setFlowSetting(flowID, 'navActiveStage', newRunItem.stage.stageID);
        setFlowSetting(flowID, 'navActiveEmbeddedFlow', null);
        history.push(`${match.url}/stages/${newRunItem.stage.stageID}`);
      }
      if (newRunItem?.flow?.flowID) {
        setFlowSetting(flowID, 'navActiveStage', null);
        setFlowSetting(flowID, 'navActiveEmbeddedFlow', newRunItem.flow.flowID);
        history.push(`${match.url}/stages/embedded/${newRunItem.flow.flowID}`);
      }
    },
    [match.url, history, onAddRunSequenceItem, setFlowSetting]
  );

  return (
    <SidebarWrapper visible={leftSidebar}>
      <SidebarHeader>
        <span />
        <Button.SidebarHideBtn
          onClick={() => setFlowSetting(flow.ID, 'leftSidebar', false)}
          title="Hide navigation"
        />
      </SidebarHeader>
      <SidebarContent>
        <Switch>
          <Route path={`${match.url}/stages`}>
            {() => (
              <Fragment>
                <SidebarNavMenu options={flowNavOpts} minHeight="160px" />
                <Separator />
                <FlowItemList
                  flowID={flow.ID}
                  baseURL={`${match.url}/stages`}
                  items={runSequence}
                  label="Stage"
                  renderItem={renderStage}
                  onRemoveItem={onRemoveRunSequenceItem}
                  onMoveItem={onReorderRunSequenceItem}
                  setFlowSetting={setFlowSetting}
                  flowSettings={flowSettings}
                />
                <SidebarFooter
                  label="a stage"
                  onAddItem={onAddStage}
                  objType="Stage"
                  flowID={flow.ID}
                />
              </Fragment>
            )}
          </Route>
          <Route path={`${match.url}/job-tables/:tableID`}>
            {({ match: innerMatch }) => {
              const { tableID } = innerMatch.params;

              const tablePath = `${match.url}/job-tables/${tableID}`;
              return (
                <SidebarNavMenu
                  options={[
                    {
                      text: `Columns`,
                      key: 'columns',
                      to: `${tablePath}/columns`,
                    },
                    {
                      text: `Import settings`,
                      key: 'import-settings',
                      to: `${tablePath}/import-settings`,
                    },
                    {
                      text: `Export settings`,
                      key: 'export-settings',
                      to: `${tablePath}/export-settings`,
                    },
                    {
                      text: `Job settings`,
                      key: 'job-settings',
                      to: `${tablePath}/job-settings`,
                    },
                    {
                      text: `Documentation`,
                      key: 'documentation',
                      to: `${tablePath}/documentation`,
                    },
                  ]}
                />
              );
            }}
          </Route>
          <Route path={`${match.url}/job-tables`}>
            {() => <SidebarNavMenu options={flowNavOpts} />}
          </Route>
          <Route path={`${match.url}/working-tables/:tableID`}>
            {({ match: innerMatch }) => {
              const { tableID } = innerMatch.params;

              const tablePath = `${match.url}/working-tables/${tableID}`;
              return (
                <SidebarNavMenu
                  options={[
                    {
                      text: `Columns`,
                      key: 'columns',
                      to: `${tablePath}/columns`,
                    },
                    {
                      text: `Documentation`,
                      key: 'documentation',
                      to: `${tablePath}/documentation`,
                    },
                  ]}
                />
              );
            }}
          </Route>
          <Route path={`${match.url}/working-tables`}>
            {() => <SidebarNavMenu options={flowNavOpts} />}
          </Route>

          <Route path={`${match.url}/tests/:testCaseKey`}>
            {({ match: innerMatch }) => {
              const { testCaseKey } = innerMatch.params;
              const testPath = `${match.url}/tests/${testCaseKey}`;
              return (
                <SidebarNavMenu
                  options={[
                    {
                      text: `Configuration`,
                      key: 'configuration',
                      to: `${testPath}`,
                    },
                  ]}
                />
              );
            }}
          </Route>

          <Route path={`${match.url}/tests`}>
            {() => <SidebarNavMenu options={flowNavOpts} />}
          </Route>
          <Route path={`${match.url}/variables`}>
            {() => <SidebarNavMenu options={flowNavOpts} />}
          </Route>
          <Route path={`${match.url}/documentation`}>
            {() => <SidebarNavMenu options={flowNavOpts} />}
          </Route>
        </Switch>
      </SidebarContent>
    </SidebarWrapper>
  );
}
